import React from "react";
import { ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faIdCard,
  faUserTag,
  faNetworkWired,
  faUserCheck,
  faCreditCard,
  faGlobe,
  faUserPlus,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import useCreateClient from "../../services/Admin/useCreateClient";


const ClientCreationPage = ({ Admin }) => {
  const {
    loading,
    formData,
    passwordError,

    userTypeOptions,
    onboardingTypeOptions,

    handleInputChange,
    handleSelectChange,
    handleSubmit,
    resetForm,
  } = useCreateClient({ Admin });

  const inputClass =
    "h-[42px] w-full rounded-md border border-gray-300 bg-white px-3 text-[15px] font-medium text-gray-800 outline-none transition placeholder:font-normal placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

  const labelClass =
    "mb-2 flex items-center text-[15px] font-bold text-gray-700";

  const sectionTitleClass = "text-[18px] font-bold text-[#2d8eff]";

  const iconClass = "mr-2 text-[#2d8eff]";

  const FormField = ({
    label,
    icon,
    name,
    type = "text",
    placeholder,
    required = false,
    children,
  }) => {
    return (
      <div className="mb-4">
        <label className={labelClass}>
          <FontAwesomeIcon icon={icon} className={iconClass} />
          {label}
          {required ? "*" : ""}
        </label>

        {children || (
          <input
            type={type}
            name={name}
            value={formData[name]}
            onChange={handleInputChange}
            placeholder={placeholder}
            required={required}
            className={inputClass}
          />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#dfedff36] py-4">
      <ToastContainer position="top-right" autoClose={5000} />

      <div className=" w-full max-w-[1600px] px-4">
        {/* Header */}
        <div className="mb-4 flex items-center">
          <FontAwesomeIcon icon={faUserPlus} className="mr-2 text-gray-600" />
          <h1 className="text-3xl font-semibold text-gray-500">
            Create New Client
          </h1>
        </div>

        <div className="w-full">
          <form onSubmit={handleSubmit}>
            {/* Client Information */}
            <div className="mb-3 rounded-md border border-gray-200 bg-white p-4">
              <div className="mb-4">
                <h5 className={sectionTitleClass}>Client Information</h5>
                <hr className="mt-2 border-gray-200" />
              </div>

              <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                <FormField
                  label="Client Name"
                  icon={faUser}
                  name="clientName"
                  placeholder="Enter client name"
                  required
                />

                <FormField
                  label="Client Number"
                  icon={faIdCard}
                  name="clientNumber"
                  placeholder="Enter client number"
                  required
                />

                <FormField label="User Type" icon={faUserTag} required>
                  <select
                    name="userType"
                    value={formData.userType}
                    onChange={handleSelectChange}
                    required
                    className={inputClass}
                  >
                    <option value="">Select user type</option>
                    {userTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField
                  label="Onboarding Type"
                  icon={faNetworkWired}
                  required
                >
                  <select
                    name="onboardingType"
                    value={formData.onboardingType}
                    onChange={handleSelectChange}
                    required
                    className={inputClass}
                  >
                    <option value="">Select onboarding type</option>
                    {onboardingTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>
            </div>

            {/* Additional Details */}
            <div className="mb-3 rounded-md border border-gray-200 bg-white p-4">
              <div className="mb-4">
                <h5 className={sectionTitleClass}>Additional Details</h5>
                <hr className="mt-2 border-gray-200" />
              </div>

              <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                <FormField
                  label="Onboarded By"
                  icon={faUserCheck}
                  name="onboardedBy"
                  placeholder="Enter onboarding agent"
                />

                <FormField
                  label="Billing Name"
                  icon={faCreditCard}
                  name="billing_name"
                  placeholder="Enter billing name"
                />

                <div className="md:col-span-2">
                  <FormField
                    label="Allowed IP Addresses"
                    icon={faGlobe}
                    name="allowed_ip"
                    placeholder="Enter allowed IP addresses (comma separated)"
                  />

                  <p className="-mt-2 text-[13px] text-gray-500">
                    Leave empty to allow all IP addresses. Use comma to separate
                    multiple IPs.
                  </p>
                </div>
              </div>
            </div>

            {/* Primary Contact */}
            <div className="rounded-md border border-gray-200 bg-white p-4">
              <div className="mb-4">
                <h5 className={sectionTitleClass}>Primary Contact</h5>
                <hr className="mt-2 border-gray-200" />
              </div>

              <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                <FormField
                  label="First Name"
                  icon={faUser}
                  name="firstName"
                  placeholder="Enter first name"
                  required
                />

                <FormField
                  label="Last Name"
                  icon={faUser}
                  name="lastName"
                  placeholder="Enter last name"
                  required
                />

                <FormField
                  label="Password"
                  icon={faLock}
                  name="password"
                  type="password"
                  placeholder="Enter password"
                  required
                />

                <div>
                  <FormField
                    label="Confirm Password"
                    icon={faLock}
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    required
                  />

                  {passwordError && (
                    <p className="-mt-2 text-[13px] font-semibold text-red-600">
                      {passwordError}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="rounded-md bg-gray-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-600"
              >
                Reset
              </button>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading && (
                  <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}

                {loading ? "Creating..." : "Create Client"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientCreationPage;